import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../Context Api/AuthContext";
import { getAccessToken, getUserData, isAuthenticated } from "../../Helpers/authHelper";
import { useNavContext } from "../../Context Api/NavContext.js";
import fallbackImage from "../../Assets/Images/no-user.png";
import {
  SVG_DASHBOARD,
  SVG_UP_ARROW,
  SVG_USER,
  SVG_LOGOUT,
  SVG_CHEVRON_DOWN,
  SVG_CHEVRON_UP,
} from "../../Assets/SVG/svg_util";
import useWindowWidth from "../../Hooks/useWindowWidth.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import useApi from "../../Hooks/useApi.js";
import { useTranslation } from "react-i18next";

const NavProfile = () => {
  const { isShowBurgerMenu, setShowBurgerMenu } = useNavContext();
  const isLoggedIn = isAuthenticated();
  const { user, logout } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  const [showList, setShowList] = useState(false);
  const [userData, setUserData] = useState(null);
  const [profileImage, setProfileImage] = useState("");

  const dropDownRef = useRef(null);
  const { request } = useApi();

  const DropDownLists = [
    { key: "my_profile", icon: <SVG_USER />, url: "/profile" },
    { key: "modules", icon: <SVG_DASHBOARD />, url: "/modules" },
    {
      key: "change_password",
      icon: <SVG_DASHBOARD />,
      url: "/change-password",
    },
    {
      key: "logout",
      icon: <SVG_LOGOUT />,
      handler: () => logout(),
    },
  ];
  
 
  useEffect(() => {
    const data = user || getUserData();
    if (data) {
      setUserData(data);
    }
  }, [user, location]); 

  
  useEffect(() => {
    let objectUrl = null; 

    const fetchProfilePicture = async () => {
      if (!userData) { 
        setProfileImage(fallbackImage);
        return;
      }
      try {
        const accessToken = getAccessToken();
        const response = await request("GET", "/v1/user/profile-picture", {
          headers: { Authorization: `Bearer ${accessToken}` },
          responseType: "blob",
        });

       
        if (response && response.type.startsWith("image/")) {
          objectUrl = URL.createObjectURL(response);
          setProfileImage(objectUrl);
        } else {
          throw new Error("Response was not an image.");
        }
      } catch (error) {
        console.error("Failed to load profile picture:", error);
        setProfileImage(fallbackImage); 
      }
    };

    fetchProfilePicture();


    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [userData, request]); 


  useEffect(() => {
    const handleClickOutSide = (event) => {
      if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
        setShowList(false);
      }
    };

    if (showList) {
      document.addEventListener("mousedown", handleClickOutSide);
    } else {
      document.removeEventListener("mousedown", handleClickOutSide);
    }
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutSide);
    };
  }, [showList]);
  

  const windowWidth = useWindowWidth();
  useEffect(() => {
 
    setShowBurgerMenu(windowWidth <= 768);
  }, [windowWidth, setShowBurgerMenu]);

  if (!isLoggedIn) return null;
  
  const handleToggleDropdown = () => setShowList((prev) => !prev);
  
  const DropdownItem = ({ item, onAction }) => {
    const content = (
      <>
        {item.icon}
        {t(`profile_menu.${item.key}`)}
      </>
    );

    const liClasses = "flex items-center gap-2 cursor-pointer p-2";

    if (item.url) {
      return (
        <Link to={item.url} onClick={onAction} className="hover:bg-gray-100 hover:text-gray-900 rounded-md">
          <li className={liClasses}>{content}</li>
        </Link>
      );
    }

    if (item.handler) {
      return (
        <div onClick={() => { item.handler(); onAction(); }} className="hover:bg-gray-100 hover:text-gray-900 rounded-md">
          <li className={liClasses}>{content}</li>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="sticky font-light" style={{ zIndex: 50 }}>
      {isShowBurgerMenu ? (
     
        <div ref={dropDownRef} className="relative md:hidden pt-4">
          <div className="flex justify-between mx-2 border-t border-t-gray-300 py-4">
            <div className="flex items-center gap-1">
              <img alt="User Logo" src={profileImage} className="w-10 h-10 rounded-full object-cover" />
              <div className="text-left font-medium text-sm text-gray-500">
                <p className="capitalize">{userData?.fullName}</p>
                <p className="text-xs">{userData?.roles}</p>
              </div>
            </div>
            <button className="mr-3" onClick={handleToggleDropdown}>
              <FontAwesomeIcon icon={faEllipsisVertical} />
            </button>
          </div>
          {showList && (
            <div className="absolute right-8 bottom-5 w-48 bg-white shadow-lg rounded-md text-[15px] font-medium">
              <ul className="space-y-1 text-gray-700 p-2 text-left">
                {DropDownLists.map((item, index) =>
                  item.key !== "dashboard" ? (
                    <DropdownItem
                      key={index}
                      item={item}
                      onAction={() => {
                        setShowList(false);
                        setShowBurgerMenu(false); 
                      }}
                    />
                  ) : null
                )}
              </ul>
            </div>
          )}
        </div>
      ) : (
     
        <nav ref={dropDownRef} className="w-full hidden md:block relative">
          <div
            className="w-full flex items-center border border-[#2f5d96] bg-[#244873] p-2 rounded-md hover:cursor-pointer"
            onClick={handleToggleDropdown}
          >
            <div className="w-14 h-14 rounded-full p-1">
              <img alt="User profile" src={profileImage} className="w-full h-full rounded-full object-cover" />
            </div>
            <div className="text-left hidden lg:block ml-2">
              <p className="text-white text-lg capitalize">{userData?.fullName}</p>
              <p className="text-white text-xs capitalize">{userData?.designation}</p>
            </div>
            <div className="ml-auto pr-1 pl-2 hidden lg:block">
              {showList ? <SVG_CHEVRON_UP /> : <SVG_CHEVRON_DOWN />}
            </div>
          </div>
          {showList && (
            <>
              <div className="absolute right-4 top-12 text-[#152f4e]">
                <SVG_UP_ARROW />
              </div>
              <div className="w-72 rounded-md text-[15px] absolute right-0 top-[5.2rem] text-white text-left">
                <ul className="space-y-2 bg-[#152f4e] p-4 rounded-b-md">
                  {DropDownLists.map((item, index) => (
                    <React.Fragment key={index}>
                      {item.url ? (
                        <Link to={item.url} onClick={() => setShowList(false)}>
                          <li className="flex items-center gap-2 cursor-pointer p-2 hover:bg-[#2f5d96] rounded-md">{item.icon}{t(`profile_menu.${item.key}`)}</li>
                        </Link>
                      ) : (
                        <li className="flex items-center gap-2 cursor-pointer p-2 hover:bg-[#2f5d96] rounded-md" onClick={() => { item.handler(); setShowList(false); }}>{item.icon}{t(`profile_menu.${item.key}`)}</li>
                      )}
                      {index === DropDownLists.length - 2 && <hr className="border-gray-500 my-2" />}
                    </React.Fragment>
                  ))}
                </ul>
              </div>
            </>
          )}
        </nav>
      )}
    </div>
  );
};

export default NavProfile;